// Session-scoped registry of AttachmentBar panels participating in page layout.
// Tracks the space each occupies from the right viewport edge (its width plus
// its rightOffset when panels tile side by side). Lets app layouts react to
// side panels that are mounted directly (not through a side panel store) —
// e.g. voyage's DefaultTemplate pushes the page content aside by the widest
// occupied strip in 'push' content mode. See sidePanelContentMode.ts for the
// push/overlay preference itself.

// Numeric twin of AttachmentBar.styles' CONTAINER_WIDTH ('441px') — the width
// panels get when they don't pass a `width` prop.
export const DEFAULT_ATTACHMENT_BAR_WIDTH = 441

const CHANGE_EVENT = 'attachmentBarPresenceChange'

const openPanels = new Map<number, number>()
let nextId = 0

const notify = () => {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

// Returns a handle used to update/unregister this panel.
export const registerAttachmentBar = (occupiedRightWidth: number): number => {
  nextId += 1
  openPanels.set(nextId, occupiedRightWidth)
  notify()
  return nextId
}

export const updateAttachmentBar = (id: number, occupiedRightWidth: number) => {
  if (!openPanels.has(id)) return
  openPanels.set(id, occupiedRightWidth)
  notify()
}

export const unregisterAttachmentBar = (id: number) => {
  if (openPanels.delete(id)) notify()
}

export const getOpenAttachmentBarCount = () => openPanels.size

// Widest right-edge strip occupied by any open panel (0 when none are open).
export const getAttachmentBarOccupiedWidth = (): number =>
  openPanels.size === 0 ? 0 : Math.max(...openPanels.values())

// Returns an unsubscribe function. Subscribers pull current values via the
// getters above.
export const subscribeAttachmentBarPresence = (callback: () => void): (() => void) => {
  window.addEventListener(CHANGE_EVENT, callback)

  return () => {
    window.removeEventListener(CHANGE_EVENT, callback)
  }
}
